import { FunctionComponent, h } from 'preact';
import { SharedLocationState } from '../../../stores/shared-location/shared-location-store';
import { SharingType, SharedLocationDisplayType } from '@bringg/types';
import { lazy, Suspense } from 'preact/compat';
import { observer } from 'mobx-react';
import Loading from '../../../components/loading/loading';
import { useCallback } from 'preact/hooks';

const ScheduleReadyPage = lazy(() => import('../../../pages/schedule-ready-page/schedule-ready-page'));
const Tracking = lazy(() => import('../../tracking/tracking'));
const UserDeliveryConfiguration = lazy(
	() => import('../../../components/user-delivery-configuration/user-delivery-configuration')
);

const SharedLocationExpired = lazy(() => import('../../shared-location-expired/shared-location-expired'));
const SharedLocationForwardPage = lazy(
	() => import('../../../pages/shared-location-forward-page/shared-location-forward-page')
);
const PickupPage = lazy(() => import('../../../pages/pickup-page/pickup-page'));

interface Props {
	sharedLocationState: SharedLocationState;
	sharingType: SharingType;
	allowCancelByEventType: boolean;

	displayScreen?: SharedLocationDisplayType;
}

const getSharedLocationScreen = (
	sharingType: SharingType,
	allowCancelByEventType: boolean,
	displayScreen: SharedLocationDisplayType
) => {
	switch (sharingType) {
		case SharingType.Reschedule:
		case SharingType.Schedule:
			return <UserDeliveryConfiguration />;

		case SharingType.CustomerPickup:
			return <PickupPage />;

		case SharingType.ScheduleReady:
			return <ScheduleReadyPage allowCancelByEventType={allowCancelByEventType} />;

		case SharingType.CustomWorkflow:
		case SharingType.NotificationByTemplateId:
			switch (displayScreen) {
				case SharedLocationDisplayType.Schedule:
					return <UserDeliveryConfiguration />;

				case SharedLocationDisplayType.Pickup:
					return <PickupPage />;

				case SharedLocationDisplayType.ScheduleReady:
					return <ScheduleReadyPage allowCancelByEventType={allowCancelByEventType} />;

				case SharedLocationDisplayType.Tracking:
					return <Tracking />;

				default:
					console.warn('Unknown requested screen', { sharingType, displayScreen });
					return <Tracking />;
			}

		default:
			return <Tracking />;
	}
};

const SharedLocationContent: FunctionComponent<Props> = observer(
	({ sharedLocationState, sharingType, allowCancelByEventType, displayScreen }: Props) => {
		const getSharedLocationContent = useCallback(() => {
			switch (sharedLocationState) {
				case SharedLocationState.Initialized:
					return getSharedLocationScreen(sharingType, allowCancelByEventType, displayScreen);

				case SharedLocationState.Forwarded:
				case SharedLocationState.Forward:
					return <SharedLocationForwardPage sharedLocationState={sharedLocationState} />;

				case SharedLocationState.Expired:
					return <SharedLocationExpired />;
			}
		}, [sharedLocationState, sharingType, allowCancelByEventType, displayScreen]);

		return <Suspense fallback={<Loading />}>{getSharedLocationContent()}</Suspense>;
	}
);

export default SharedLocationContent;
